<template>
  <ChatModal
    name="group-profile"
    :title="$t('chat.groupProfileTitle')"
    @before-open="beforeOpen"
  >
    <div v-if="chat" slot="default">
      <div :class="$style.header">
        <AvatarGroup
          :chat="chat"
          :class="$style.avatar"
          v-viewer="{ navbar: false, rotatable: false, title: false }"
        />
        <div :class="$style.info">
          <div :class="$style.name">{{ chatName }}</div>
          <div :class="$style.meta">{{ $t('chat.members') }}: {{ chat.participants.length }}</div>
          <div v-if="chat.description" :class="$style.description">{{ chat.description }}</div>
        </div>
      </div>
      <div v-if="isSelfAdmin" :class="$style.headerActions">
        <Fab @click.native="changeGroupName" :class="$style.headerActionBtn">
          <BaseIcon name="pencil-alt" />
        </Fab>
        <FileInput
          accept="image/*"
          @input="onFileChange"
        >
          <Fab tag="div" :class="$style.headerActionBtn">
            <BaseIcon name="camera" />
          </Fab>
        </FileInput>
      </div>
      <div :class="$style.section">
        <div>
          <span @click="addMembers" class="link">+ {{ $t('chat.addParticipantsButton') }}</span>
        </div>
        <UserList
          :users="users"
          :chatId="chatId"
        >
          <template v-slot:role="{ user }">
            <span v-if="isUserOwner(user.userId)" :class="$style.role">({{ $t('chat.owner') }})</span>
            <span v-else-if="isUserAdmin(user.userId)" :class="$style.role">({{ $t('chat.admin') }})</span>
          </template>
          <template v-slot:default="{ user }">
            <div>
              <span
                v-if="isUserOwner(user.userId) && isSelfOwner"
                :class="[$style.action, $style.red]"
                @click.stop="leaveChat(user)"
              >{{ $t('chat.delete') }}</span>
              <span
                v-else-if="isSelfAdmin && !isUserMy(user.userId) && !isUserOwner(user.userId)"
                :class="[$style.action, $style.red]"
                @click.stop="removeUser(user)"
              >{{ $t('chat.removeUser') }}</span>
            </div>
            <!-- <div v-if="isSelfOwner && !isUserOwner(user.userId)">
              <span :class="$style.action" @click.stop="onChangeModerator(user.userId)">{{ displayChangeAdminButton(user.userId) }}</span>
            </div> -->
          </template>
        </UserList>
      </div>
    </div>
  </ChatModal>
</template>

<script>
import ChatModal from '@/components/modals/ChatModal'
import AvatarGroup from '@/components/AvatarGroup'
import Fab from '@/components/Fab'
import UserList from '@/components/UserList'
import { mapGetters, mapActions } from 'vuex'
import FileInput from '@/components/FileInput'

export default {
  name: 'group-profile-popup',
  components: {
    ChatModal,
    AvatarGroup,
    UserList,
    FileInput,
    Fab
  },
  data () {
    return {
      chatId: null
    }
  },
  computed: {
    ...mapGetters({
      selfUser: 'auth/user',
      chatById: 'entities/chats/getById',
      getParticipant: 'entities/participants/find'
    }),
    chat () {
      return this.chatById(this.chatId)
    },
    users () {
      if (!this.chat) return
      return this.chat.participantsUsers
    },
    chatName () {
      return this.chat.name
    },
    isSelfOwner () {
      if (!this.chat) return
      return this.isUserOwner(this.selfUser.id)
    },
    isSelfAdmin () {
      if (!this.chat) return
      return this.isUserAdmin(this.selfUser.id)
    }
  },
  methods: {
    beforeOpen ({ params: { chatId } }) {
      this.chatId = chatId
    },
    ...mapActions({
      editChatPhoto: 'entities/chats/editPhoto',
      removeParticipants: 'entities/participants/remove', // 'entities/chats/removeParticipants'
      changeModerator: 'entities/participants/changeModerator'
    }),
    addMembers () {
      this.$modal.show('add-users-to-chat', { chatId: this.chatId })
    },
    removeUser (user) {
      this.$modal.show('confirm', {
        content: this.$t('chat.removeUserConfirmation', { name: user.displayName }),
        onSubmit: () => {
          this.removeParticipants({
            chatId: this.chat.id,
            userId: user.userId
          })
        }
      })
    },
    isUserMy (userId) {
      if (!this.chat) return false
      return this.selfUser.id === userId
    },
    isUserOwner (userId) {
      if (!this.chat) return false
      return this.chat.owner === userId
    },
    isUserAdmin (userId) {
      if (!this.chat) return false
      const participant = this.chat.participants.find(participant => participant.userId === userId)
      return this.isUserOwner(userId) || (participant && participant.isModerator)
    },
    leaveChat () {
      this.$modal.show('delete-chat')
    },
    changeGroupName () {
      this.$modal.show('edit-group')
    },
    onFileChange (file) {
      this.$modal.show('cropper', {
        aspectRatio: 1,
        file,
        onDone: this.onImageCropped
      })
    },
    onImageCropped (canvas) {
      canvas.toBlob(blob => {
        const file = new File([blob], 'chat_image.jpeg')
        this.editChatPhoto({
          id: this.chatId,
          photo: file
        })
      }, 'image/jpeg', 0.8)
    },
    displayChangeAdminButton (userId) {
      if (!this.isUserAdmin(userId)) {
        return this.$t('chat.makeAdmin')
      }
      return this.$t('chat.removeAdmin')
    },
    onChangeModerator (userId) {
      this.changeModerator({
        chatId: this.chatId,
        userId
      })
    }
  }
}
</script>

<style lang="scss" module>
.header {
  background-color: $main-bg-colored;
  display: flex;
  padding: 0 20px 25px;
}
.headerActions {
  position: relative;
  top: -20px;
  display: flex;
  justify-content: flex-end;
  padding-right: 60px;
}
.headerActionBtn {
  margin-left: 24px;
}

.avatar {
  margin-right: 12px;
  flex-shrink: 0;
  :global(img) {
    cursor: pointer;
  }
}
.info {
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: $text-lightest;
}
.name {
  font-size: 16px;
  font-weight: 500;
}
.meta,
.description {
  font-weight: 300;
  margin-top: 2px;
}

.role {
  font-size: 12px;
  color: $text-mid;
  font-weight: 300;
}
.action {
  font-size: 12px;
  color: $text-success;
  &:hover {
    border-bottom: 1px solid $text-success;
  }

  &.red {
    color: $text-error;
    &:hover {
      border-color: $text-error;
    }
  }
}

.section {
  padding: 10px 10px 20px 82px;
}
</style>
